/**
 * Alpine Stores v2.0.0 for Feelrooty
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * IIFE providing Alpine stores (must be defer, not module).
 *
 * Stores:
 * - $store.selection: Page-level selection state (what will be added to cart)
 * - $store.cart: Cart state + actions
 * - $store.ui: UI state (drawers, modals)
 * - $store.currency: Currency conversion helpers
 *
 * Magic Helpers:
 * - $selection: Shortcut to $store.selection
 * - $money: Format cents to money string
 * - $currency: Shortcut to $store.currency
 *
 * Architecture (issue #20):
 * - $store.cart / $store.discount: READ-ONLY reactive projections of <cart-engine>
 *   state (synced on store:cart:update). No write methods live here.
 * - $cart (magic → window.cartEngine): the cart action surface (add, changeItem,
 *   removeItem, applyDiscount, …).
 * - $store.selection: single source of truth for ATC buttons; addToCart() calls
 *   window.cartEngine.add().
 *
 * Access patterns:
 * - Templates (read):   x-text="$store.cart.formattedTotal"
 * - Templates (action): @click="$cart.add({ id })" / @click="$store.selection.addToCart()"
 *
 * WHY IIFE (not ES Module):
 * Alpine.js fires alpine:init during defer execution. ES Modules execute
 * AFTER defer scripts. IIFE ensures stores register before Alpine initializes.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════════
  // LOGGING
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // Delegates to the frozen core logger (assets/fw-logger.js, loaded before
  // this file — see layout/theme.liquid). The internal call signature
  // log.level(ns, msg, data) is preserved so existing call sites stay
  // unchanged; entries land in Theme.Logger's ring buffer
  // (Theme.Logger.dump() / tail()). Console printing is gated by
  // Theme.Logger.enable() + filter(); errors always print.
  //
  // ═══════════════════════════════════════════════════════════════════════════════

  window.Theme = window.Theme || {};

  // Logger loads before this file (defer order). Fall back to a quiet shim if
  // it didn't — a wiring bug degrades logging instead of breaking cart methods
  // (mirrors fw-events.js's own fallback shim).
  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };

  // Null-prototype cache: createSelection accepts caller-supplied namespaces,
  // so keys must not collide with Object.prototype members.
  const _nsCache = Object.create(null);
  const _ns = (ns) => _nsCache[ns] || (_nsCache[ns] = Logger.ns(ns));

  const log = {
    debug: (ns, msg, data) => _ns(ns).debug(msg, data),
    info:  (ns, msg, data) => _ns(ns).info(msg, data),
    warn:  (ns, msg, data) => _ns(ns).warn(msg, data),
    error: (ns, msg, data) => _ns(ns).error(msg, data),
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // EVENTS
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // Event classes live in assets/fw-events.js (window.Theme.events), loaded
  // before this file. Every dispatch goes through a ThemeEvent subclass so it
  // auto-logs to Theme.Logger.ns('events'). Names are frozen — see the
  // registered vocabulary in assets/fw-events.js.
  //
  // Deliberate degraded mode: if fw-events failed to load (wiring bug), fall
  // back to plain named CustomEvents so cart methods and listeners keep
  // working — only the auto-logging is lost. The name literals below exist
  // ONLY for this fallback; fw-events.js stays the single source of truth
  // (the registry parity test in tests/e2e/smoke.spec.ts pins it).
  //
  // ═══════════════════════════════════════════════════════════════════════════════

  const themeEvents = window.Theme.events || (() => {
    log.error('stores', 'Theme.events missing — fw-events.js not loaded; dispatching unlogged fallback events');
    class FallbackThemeEvent extends CustomEvent {
      constructor(name, detail = {}, options = {}) {
        super(name, { bubbles: options.bubbles !== false, detail });
      }
    }
    const mk = (name) => class extends FallbackThemeEvent {
      constructor(detail = {}, options = {}) { super(name, detail, options); }
    };
    return {
      ThemeEvent: FallbackThemeEvent,
      CartUpdateEvent: mk('store:cart:update'),
      CartErrorEvent: mk('store:cart:error'),
      CartAddedEvent: mk('store:cart:added'),
      DiscountSuccessEvent: mk('discount-success'),
      DiscountErrorEvent: mk('discount-error'),
      classFor: () => null,
    };
  })();

  // Only ThemeEvent is used here now (the generic Theme.event helper). The cart
  // event classes are dispatched by <cart-engine>, not this file (issue #20).
  const { ThemeEvent } = themeEvents;

  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Format cents to money string using Shopify's official formatter
   * Falls back to custom implementation if Shopify.formatMoney not available
   */
  const formatMoney = (cents, format) => {
    // Use Shopify's official formatter if available (from shopify-money.js)
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const formatStr = format || Shopify.money_format || window.Theme?.shopify?.moneyFormat;
      return Shopify.formatMoney(cents, formatStr);
    }

    // Fallback: custom implementation
    if (typeof cents === 'string') cents = parseInt(cents.replace(/[^0-9-]/g, ''));
    if (isNaN(cents) || cents == null) return '';

    const formatStr = format || window.Theme?.shopify?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');

    return formatStr
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  window.Theme.formatMoney = formatMoney;

  // ═══════════════════════════════════════════════════════════════════════════════
  // OBSERVABILITY HELPERS
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // Theme.event(name, detail) — instrumented dispatch helper.
  // Thin wrapper over the ThemeEvent path (fw-events.js): construction
  // auto-logs to Theme.Logger.ns('events'), then the event dispatches on
  // window. Registered names route through their owning event class;
  // unregistered names fall back to the base ThemeEvent (still logged).
  // Components MUST use this instead of raw window.dispatchEvent for store:* events.
  //
  // (The old storeEvents.track side effect was dropped with logger.js — the
  // storeLogs persister was its only real consumer; the bus's PostHog track
  // plugin is a no-op since the theme never loads posthog.)
  //
  // ═══════════════════════════════════════════════════════════════════════════════

  window.Theme.event = (name, detail = {}) => {
    const Cls = themeEvents.classFor(name);
    window.dispatchEvent(Cls ? new Cls(detail) : new ThemeEvent(name, detail));
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // SNAPSHOT — single-call state dump for AI/Playwright introspection
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // Usage:
  //   In console: JSON.stringify(Theme.snapshot(), null, 2)
  //   In Playwright: await page.evaluate(() => Theme.snapshot())
  //
  // ═══════════════════════════════════════════════════════════════════════════════

  window.Theme.snapshot = () => {
    const safeStore = (name, mapper) => {
      try {
        if (typeof Alpine === 'undefined') return null;
        const s = Alpine.store(name);
        if (!s) return null;
        return mapper ? mapper(s) : { ...s };
      } catch (_) { return null; }
    };

    return {
      pageData: window.Theme.pageData || null,
      stores: {
        selection: safeStore('selection', (s) => ({
          productId: s.productId, variantId: s.variantId, quantity: s.quantity,
          sellingPlanId: s.sellingPlanId, price: s.price, compareAtPrice: s.compareAtPrice,
          formattedPrice: s.formattedPrice, available: s.available, loading: s.loading,
          properties: s.properties
        })),
        bundle: safeStore('bundle', (s) => ({
          quantity: s.quantity, slots: [...s.slots], isBundle: s.isBundle,
          bundleVariantId: s.bundleVariant ? s.bundleVariant.id : null,
          gifts: s.gifts.map(g => g.productId), properties: s.properties
        })),
        cart: safeStore('cart', (s) => ({
          item_count: s.item_count, total_price: s.total_price,
          items: (s.items || []).map(i => ({ key: i.key, qty: i.quantity, title: i.title })),
          discount_codes: s.discount_codes
        })),
        ui: safeStore('ui'),
        currency: safeStore('currency', (s) => ({
          shop: s.shopCurrency, presentment: s.presentment, rate: s.rate
        })),
      },
      recentEvents: window.Theme.Logger ? window.Theme.Logger.tail(50) : [],
      url: location.href,
      template: document.documentElement.dataset.template || null,
    };
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ALPINE STORES SETUP
  // ═══════════════════════════════════════════════════════════════════════════════

  const setupAlpineStores = () => {
    if (typeof Alpine === 'undefined') {
      log.error('stores', 'Alpine not found!');
      return;
    }

    log.info('stores', 'Setting up Alpine stores...');

    // ─────────────────────────────────────────────────────────────────────────────
    // $money Magic - Format cents to money string
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.magic('money', () => (cents) => formatMoney(cents));

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.ui - UI state for drawers/modals
    // ─────────────────────────────────────────────────────────────────────────────

    // Cart open/close policy lives on the drawer surface itself (issue #18): the
    // drawer opens via its own `store:cart:added` subscription, so the store no
    // longer tracks `cartOpen` or drives the drawer (the old Alpine.effect called
    // a public `cartDrawer.open()` that never existed — CartDrawer only has a
    // private `#open()`). menuOpen/searchOpen remain for future surface state.
    Alpine.store('ui', {
      menuOpen: false,
      searchOpen: false
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // Selection Factory - Creates selection objects with state + loading + addToCart
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Selection is the complete unit: what to add + loading state + action.
    // Same interface whether global ($store.selection) or local (x-data="selection({...})").
    //
    // For main product: global $store.selection, multiple ATC buttons share loading (OK!)
    // For upsells: local selection via x-data, each has isolated loading
    //
    // Usage:
    //   Main product: @click="$store.selection.addToCart()" :disabled="!$store.selection.canAdd"
    //   Upsell: x-data="selection({ variantId: 123, price: 3500 })" @click="addToCart()"
    //
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Create a selection object with state, loading, and addToCart
     * @param {Object} config - Initial selection data
     * @param {string} namespace - Logging namespace
     * @returns {Object} Selection object
     */
    const createSelection = (config = {}, namespace = 'selection') => ({
      // ─────────────────────────────────────────────────────────────────────────
      // State
      // ─────────────────────────────────────────────────────────────────────────
      productId: config.productId ?? null,
      variantId: config.variantId ?? null,
      quantity: config.quantity ?? 1,
      sellingPlanId: config.sellingPlanId ?? null,
      price: config.price ?? null,  // null until initialized, prevents €0,00 flash
      compareAtPrice: config.compareAtPrice ?? null,
      available: config.available !== false,
      // Line properties sent with the add (e.g. the bundle's _bundleData / _gifts
      // from $store.bundle). null = plain variant add.
      properties: config.properties ?? null,

      // Loading state - lives on selection, shared for same selection (correct!)
      loading: false,

      // ─────────────────────────────────────────────────────────────────────────
      // Getters
      // ─────────────────────────────────────────────────────────────────────────

      get formattedPrice() {
        // Return null if price not initialized, so x-text fallback kicks in
        if (this.price == null) return null;
        return formatMoney(this.price);
      },

      get formattedCompareAtPrice() {
        return this.compareAtPrice ? formatMoney(this.compareAtPrice) : null;
      },

      get hasDiscount() {
        return this.compareAtPrice && this.compareAtPrice > this.price;
      },

      get canAdd() {
        return this.variantId && this.available && !this.loading;
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Actions
      // ─────────────────────────────────────────────────────────────────────────

      /**
       * Set selection directly (used by quantity cards for bundles)
       * @param {Object} data - Selection data
       */
      set(data) {
        log.info(namespace, 'Setting selection', data);

        if (data.productId !== undefined) this.productId = data.productId;
        if (data.variantId !== undefined) this.variantId = data.variantId;
        if (data.quantity !== undefined) this.quantity = data.quantity;
        if (data.sellingPlanId !== undefined) this.sellingPlanId = data.sellingPlanId;
        if (data.price !== undefined) this.price = data.price;
        if (data.compareAtPrice !== undefined) this.compareAtPrice = data.compareAtPrice;
        if (data.available !== undefined) this.available = data.available;
        if (data.properties !== undefined) this.properties = data.properties;

        log.debug(namespace, 'Selection updated', {
          productId: this.productId,
          variantId: this.variantId,
          price: this.price
        });
      },

      /**
       * Update just the selling plan (used by subscription toggle)
       * @param {number|null} planId - Selling plan ID or null for one-time
       * @param {number} price - Price with this plan
       * @param {number|null} compareAtPrice - Compare at price (if discount)
       */
      setSellingPlan(planId, price, compareAtPrice = null) {
        log.info(namespace, 'Setting selling plan', { planId, price });

        this.sellingPlanId = planId;
        this.price = price;
        if (compareAtPrice !== null) {
          this.compareAtPrice = compareAtPrice;
        }
      },

      /**
       * Add this selection to cart
       * Loading state is managed here - shared for same selection (correct!)
       */
      async addToCart() {
        if (this.loading) return null;

        if (!this.variantId) {
          log.warn(namespace, 'No variant selected');
          return null;
        }

        if (!this.available) {
          log.warn(namespace, 'Variant not available');
          return null;
        }

        log.info(namespace, 'Adding to cart', {
          productId: this.productId,
          variantId: this.variantId,
          quantity: this.quantity,
          sellingPlanId: this.sellingPlanId
        });

        this.loading = true;

        try {
          if (!window.cartEngine) return null;
          const result = await window.cartEngine.add({
            id: this.variantId,
            quantity: this.quantity,
            selling_plan: this.sellingPlanId,
            properties: this.properties || undefined
          });
          return result;
        } catch (err) {
          log.error(namespace, 'Add to cart failed', err);
          return null;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Reset to initial state
       */
      reset() {
        log.debug(namespace, 'Resetting selection');
        this.productId = null;
        this.variantId = null;
        this.quantity = 1;
        this.sellingPlanId = null;
        this.price = 0;
        this.compareAtPrice = null;
        this.available = true;
        this.properties = null;
        this.loading = false;
      },

      init() {
        log.info(namespace, 'Selection initialized', {
          productId: this.productId,
          variantId: this.variantId,
          price: this.price
        });
      }
    });

    // Expose factory for advanced use cases
    window.Theme.createSelection = createSelection;

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.selection - Global selection for main product
    //
    // Seeded from window.Theme.pageData.product (set by snippets/page-data.liquid)
    // when present, so the ATC button has a valid initial price/variant before
    // quantity-cards' x-data init runs and overwrites it on $nextTick.
    // ─────────────────────────────────────────────────────────────────────────────

    const buildInitialSelection = () => {
      const p = window.Theme?.pageData?.product;
      if (!p) return {};
      const v = (p.variants || []).find(x => x.id === p.currentVariantId);
      if (!v) return { productId: p.productId };
      return {
        productId: p.productId,
        variantId: v.id,
        quantity: 1,
        price: v.price,
        compareAtPrice: v.compareAtPrice,
        available: v.available,
        sellingPlanId: null,
      };
    };

    Alpine.store('selection', createSelection(buildInitialSelection(), 'store.selection'));

    // ─────────────────────────────────────────────────────────────────────────────
    // selection() Component - Local selection for upsells
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Usage: x-data="selection({ variantId: 123, price: 3500, available: true })"
    //
    // Same interface as $store.selection, but isolated per-component.
    // Each upsell has its own loading state.
    //
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.data('selection', (config = {}) => createSelection(config, 'selection.local'));

    // ─────────────────────────────────────────────────────────────────────────────
    // $selection Magic - Shortcut to $store.selection
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.magic('selection', () => Alpine.store('selection'));

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.currency - Currency conversion using Shopify's currencies.js
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Single source of truth for currency conversion across all components.
    // Uses Shopify's /services/javascripts/currencies.js (updates twice daily).
    //
    // Usage:
    //   $store.currency.rate           → Current shop→presentment rate
    //   $store.currency.convert(5000)  → Convert 5000 cents from shop to presentment
    //   $store.currency.shopCurrency   → Shop's base currency (e.g., 'EUR')
    //   $store.currency.presentment    → Customer's currency (e.g., 'HUF')
    //
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.store('currency', {
      // Config from Theme (set in theme.liquid)
      get shopCurrency() {
        return window.Theme?.shopify?.shopCurrency || 'EUR';
      },

      get presentment() {
        return window.Theme?.shopify?.presentmentCurrency || this.shopCurrency;
      },

      // Check if Currency.rates is available
      get ready() {
        return typeof Currency !== 'undefined' && Currency.rates;
      },

      // Get conversion rate from shop currency to presentment currency
      // e.g., EUR→HUF ≈ 380
      get rate() {
        if (!this.ready) return 1;
        try {
          const baseRate = Currency.rates[this.shopCurrency];
          const targetRate = Currency.rates[this.presentment];
          if (baseRate && targetRate && targetRate > 0) {
            return baseRate / targetRate;
          }
        } catch (e) {
          log.error('currency', 'Rate calculation failed', e);
        }
        return 1;
      },

      // Convert amount from shop currency to presentment currency
      // Amount should be in cents/minor units
      convert(amount) {
        return Math.round(amount * this.rate);
      },

      // Convert shop-currency cents → presentment cents, snapped to the whole
      // currency unit (no minor units). Used for "from"-style price displays
      // where converted figures read cleaner without decimals. For EUR
      // (rate === 1) callers should bypass this and show the exact authored value.
      convertWhole(amount) {
        const converted = amount * this.rate;
        return Math.round(converted / 100) * 100;
      },

      // Convert between any two currencies
      convertBetween(amount, fromCurrency, toCurrency) {
        if (!this.ready) return amount;
        try {
          const fromRate = Currency.rates[fromCurrency];
          const toRate = Currency.rates[toCurrency];
          if (fromRate && toRate && toRate > 0) {
            return Math.round(amount * (fromRate / toRate));
          }
        } catch (e) {
          log.error('currency', 'Conversion failed', e);
        }
        return amount;
      }
    });

    // Magic helper for easy access: $currency.rate, $currency.convert(5000)
    Alpine.magic('currency', () => Alpine.store('currency'));

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.cart - Cart state + actions
    // ─────────────────────────────────────────────────────────────────────────────

    const ssrCart = window.Theme?.cartData || {};

    // $cart magic — the action surface. Resolves to <cart-engine> (the single
    // writer) at access time so it stays correct across engine re-mounts.
    // Templates: @click="$cart.add({ id })" / $cart.applyDiscount(code) (issue #20).
    Alpine.magic('cart', () => window.cartEngine);

    Alpine.store('cart', {
      // State (synced from SSR)
      items: ssrCart.items || [],
      item_count: ssrCart.item_count || 0,
      total_price: ssrCart.total_price || 0,
      total_discount: ssrCart.total_discount || 0,
      currency: ssrCart.currency || 'EUR',

      // Discount state
      discount_codes: ssrCart.discount_codes || [],
      discountLoading: false,
      discountError: null,

      // UI State (projected from <cart-engine> snapshot)
      loading: false,
      itemLoading: {},
      error: null,

      // ─────────────────────────────────────────────────────────────────────────
      // Getters
      // ─────────────────────────────────────────────────────────────────────────

      get isEmpty() {
        return this.item_count === 0;
      },

      get hasItems() {
        return this.item_count > 0;
      },

      get formattedTotal() {
        return formatMoney(this.total_price);
      },

      get formattedDiscount() {
        return this.total_discount > 0 ? formatMoney(this.total_discount) : null;
      },

      /**
       * Compute original subtotal (before all discounts)
       * Used by cart-savings component to show strikethrough price
       */
      get originalSubtotal() {
        if (!this.items || !this.items.length) return this.total_price;
        return this.items.reduce((sum, item) => {
          const qty = item.quantity || 0;
          const unitFinal = item.final_price ?? item.price ?? 0;
          let unitOrig = item.original_price ?? unitFinal;
          // Include selling plan compare_at_price if higher
          const planCompare = item.selling_plan_allocation?.compare_at_price ?? 0;
          if (planCompare > unitOrig) unitOrig = planCompare;
          // Never below final
          if (unitOrig < unitFinal) unitOrig = unitFinal;
          return sum + (unitOrig * qty);
        }, 0);
      },

      get savedAmount() {
        return Math.max(0, this.originalSubtotal - this.total_price);
      },

      get hasSavings() {
        return this.savedAmount > 0;
      },

      get formattedOriginalSubtotal() {
        return formatMoney(this.originalSubtotal);
      },

      get formattedSavedAmount() {
        return formatMoney(this.savedAmount);
      },

      get isAnyItemLoading() {
        return Object.keys(this.itemLoading).length > 0;
      },

      isItemLoading(key) {
        return this.itemLoading[key] === true;
      },

      formatMoney(cents) {
        return formatMoney(cents);
      },

      /**
       * Get only applicable discount codes (for display)
       */
      get appliedDiscounts() {
        return (this.discount_codes || []).filter(dc => dc.applicable !== false);
      },

      /**
       * Check if any discounts are applied
       */
      get hasDiscounts() {
        return this.appliedDiscounts.length > 0;
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Sync from <cart-engine> (issue #20 cart-engine migration)
      //
      // <cart-engine> (assets/cart-engine.js) is the single writer/owner of cart
      // truth. It ships its full snapshot (getState()) on every store:cart:update;
      // this projection copies it wholesale so every read getter above stays
      // reactive. No per-field hand-copy — new snapshot fields project for free.
      // ─────────────────────────────────────────────────────────────────────────

      _syncFromEngine(state) {
        if (!state) return;
        Object.assign(this, state);
      },

      // Write actions are NOT on this store — it is a READ-ONLY projection.
      // Callers act via $cart (Alpine magic → window.cartEngine) or window.cartEngine
      // directly. This mirrors the framework's $store.cart / $cart split (issue #20).

      init() {
        log.info('store.cart', 'Cart projection initialized', { item_count: this.item_count });

        // <cart-engine> is the single dispatcher of store:cart:update now; sync
        // the projection from its snapshot on every update (unidirectional flow —
        // the projection never dispatches, so no loop guard needed).
        document.addEventListener('store:cart:update', (ev) => {
          try {
            const cart = ev?.detail?.cart;
            if (cart) this._syncFromEngine(cart);
          } catch (_) {}
        });

        // The engine module may connect before or after alpine:init; take a
        // one-shot snapshot if it's already up so first paint matches truth.
        if (window.cartEngine && typeof window.cartEngine.getState === 'function') {
          this._syncFromEngine(window.cartEngine.getState());
        }
      }
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // cartItem Component - Cart item with reactive bindings
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Usage: x-data="cartItem('line-key-here')"
    //
    // Provides reactive access to a cart item's data from $store.cart.items.
    // SSR renders initial values, Alpine x-text takes over for reactive updates.
    //
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // shippingBar Component - Free shipping progress bar
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Usage: x-data="shippingBar(5000, 'FREESHIP,VIP')"
    //
    // Reactive shipping bar using Shopify.currency.rate for multi-currency.
    // The second argument lists discount codes that grant free shipping on
    // their own (blocks/shipping-bar.liquid setting): while one is applied and
    // applicable, the bar is full and the "you have" copy shows whatever the
    // total. The code must be a free shipping discount in Shopify admin.
    //
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.data('shippingBar', (baseThreshold, freeShippingCodes = '') => ({
      // Base threshold in shop currency (cents), e.g., 5000 = 50 EUR
      baseThreshold: baseThreshold,

      // Upper-cased codes that grant free shipping regardless of the total
      codes: String(freeShippingCodes || '').split(',').map((c) => c.trim().toUpperCase()).filter(Boolean),

      // Threshold converted to presentment currency via $store.currency
      get threshold() {
        return Alpine.store('currency').convert(this.baseThreshold);
      },

      // One of the configured codes is applied to the cart (and applicable)
      get viaCode() {
        if (!this.codes.length) return false;
        const applied = Alpine.store('cart')?.appliedDiscounts || [];
        return applied.some((dc) => this.codes.includes(String(dc.code).toUpperCase()));
      },

      get progress() {
        if (this.viaCode) return 100;
        const total = Alpine.store('cart')?.total_price || 0;
        if (this.threshold <= 0) return 0;
        return Math.min(100, Math.round((total / this.threshold) * 100));
      },

      get remaining() {
        if (this.viaCode) return 0;
        const total = Alpine.store('cart')?.total_price || 0;
        return Math.max(0, this.threshold - total);
      },

      get hasShipping() {
        return this.remaining === 0;
      },

      init() {
        const currency = Alpine.store('currency');
        log.debug('shippingBar', 'Initialized', {
          baseThreshold: this.baseThreshold,
          codes: this.codes,
          rate: currency.rate,
          threshold: this.threshold,
          presentment: currency.presentment
        });
      }
    }));

    // ─────────────────────────────────────────────────────────────────────────────
    // (Removed) freeShippingBadge Alpine.data
    //
    // The free-shipping badge is now selection-driven, not threshold-driven —
    // see snippets/free-shipping-badge.liquid. It uses inline x-data to check
    // whether $store.selection.productId is the line's bundle product (resolved
    // by snippets/bundle-handle.liquid). No FX, no cart math, no shared
    // Alpine.data registration needed.
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // discountCode Component - Discount code input form
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Usage: x-data="discountCode()"
    //
    // Simple component for discount code input that delegates to $store.cart
    //
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.data('discountCode', () => ({
      code: '',

      get loading() {
        return Alpine.store('cart').discountLoading;
      },

      get error() {
        return Alpine.store('cart').discountError;
      },

      get appliedDiscounts() {
        return Alpine.store('cart').appliedDiscounts;
      },

      async apply() {
        if (!this.code.trim() || !window.cartEngine) return;
        const ok = await window.cartEngine.applyDiscount(this.code);
        if (ok) this.code = '';
      },

      async remove(code) {
        if (window.cartEngine) await window.cartEngine.removeDiscount(code);
      },

      clearError() {
        if (window.cartEngine) window.cartEngine.clearDiscountError();
      },

      init() {
        log.debug('discountCode', 'Component initialized');
      }
    }));

    Alpine.data('cartItem', (lineKey) => ({
      lineKey: lineKey,
      _cachedItem: null,

      /**
       * Get the cart item by line key from $store.cart.items
       * Caches last known item data to prevent €0,00 flash during removal
       */
      get item() {
        const cart = Alpine.store('cart');
        const found = cart?.items?.find(i => i.key === this.lineKey);
        if (found) {
          // Item exists in cart - update cache and return
          this._cachedItem = { ...found };
          return found;
        }
        // Item not found (being removed) - return cached data to prevent €0,00 flash
        return this._cachedItem || {};
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Getters - Computed values for template bindings
      // ─────────────────────────────────────────────────────────────────────────

      get title() {
        return this.item.title || '';
      },

      get quantity() {
        return this.item.quantity || 0;
      },

      get formattedPrice() {
        const price = this.item.final_price ?? this.item.price ?? 0;
        return formatMoney(price);
      },

      get formattedOriginalPrice() {
        let original = this.item.original_price ?? this.item.price ?? 0;
        const planCompare = this.item.selling_plan_allocation?.compare_at_price;
        if (planCompare && planCompare > original) {
          original = planCompare;
        }
        return formatMoney(original);
      },

      get unitSavedCents() {
        const final = this.item.final_price ?? this.item.price ?? 0;
        let original = this.item.original_price ?? final;
        const planCompare = this.item.selling_plan_allocation?.compare_at_price;
        if (planCompare && planCompare > original) {
          original = planCompare;
        }
        return Math.max(0, original - final);
      },

      get formattedSavings() {
        return formatMoney(this.unitSavedCents);
      },

      get hasSavings() {
        return this.unitSavedCents > 0;
      },

      get planName() {
        return this.item.selling_plan_allocation?.selling_plan?.name || '';
      },

      get hasPlan() {
        return !!this.planName;
      },

      get imageUrl() {
        return this.item.image ? `${this.item.image}&width=320` : '';
      },

      get isLoading() {
        return Alpine.store('cart').isItemLoading(this.lineKey);
      },

      init() {
        log.debug('cartItem', 'Component initialized', { lineKey: this.lineKey });
      }
    }));

    log.info('stores', 'All Alpine stores and components registered');
  };

  // Setup when Alpine is ready
  document.addEventListener('alpine:init', setupAlpineStores);

  log.info('stores', 'Alpine stores v2.0.0 loaded (waiting for alpine:init)');
})();
